// Set a rider's plate — 0279. Luigi, Slack 2026-10-01: the Hub Manager enters
// each rider's VERIFIED plate on the dashboard. Their own hub's riders, or any
// rider for an admin; `set_rider_vehicle` enforces that and audits it.
//
// No plate format is checked (0150: LTO formats change, none is approved), so
// the field only trims and upper-cases what is typed — the server does the same.

import { useState } from "react";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@bednbody/ui/components/field";
import { Input } from "@bednbody/ui/components/input";
import { toast } from "sonner";
import { type RiderVehicle, useSetRiderVehicle } from "../../staff-media/staff-media.hooks";

interface SetPlateDialogProps {
  riderId: string;
  riderName: string;
  current: RiderVehicle | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SetPlateDialog({
  riderId,
  riderName,
  current,
  open,
  onOpenChange,
}: SetPlateDialogProps) {
  const setVehicle = useSetRiderVehicle();
  const [plate, setPlate] = useState(current?.plate ?? "");
  const [vehicleType, setVehicleType] = useState(current?.vehicleType ?? "Motorcycle");
  const plateId = `plate-${riderId}`;
  const typeId = `vehicle-type-${riderId}`;
  const trimmed = plate.trim();

  function change(next: boolean) {
    if (next) {
      // Start from what is on record each time the dialog opens.
      setPlate(current?.plate ?? "");
      setVehicleType(current?.vehicleType ?? "Motorcycle");
    }
    onOpenChange(next);
  }

  function save() {
    if (!trimmed) return;
    setVehicle.mutate(
      { riderId, plate: trimmed, vehicleType: vehicleType.trim() || "Motorcycle" },
      {
        onSuccess: () => {
          toast.success(`Plate saved for ${riderName}`);
          onOpenChange(false);
        },
        onError: (error) =>
          toast.error("Couldn't save the plate", {
            description: error instanceof Error ? error.message : undefined,
          }),
      },
    );
  }

  return (
    <Dialog onOpenChange={change} open={open}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Set plate · {riderName}</DialogTitle>
          <DialogDescription>
            The verified plate of the vehicle this rider arrives on. Therapists see it at pick-up.
            Every change is kept in the Audit Log.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel htmlFor={plateId}>Plate number</FieldLabel>
            <Input
              autoComplete="off"
              className="uppercase"
              id={plateId}
              maxLength={20}
              onChange={(event) => setPlate(event.target.value)}
              placeholder="e.g. ABC 1234"
              required
              type="text"
              value={plate}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={typeId}>Vehicle</FieldLabel>
            <Input
              id={typeId}
              maxLength={40}
              onChange={(event) => setVehicleType(event.target.value)}
              type="text"
              value={vehicleType}
            />
            <FieldDescription>Motorcycle unless it's something else.</FieldDescription>
          </Field>
        </div>

        <DialogFooter>
          <Button disabled={setVehicle.isPending} onClick={() => change(false)} variant="outline">
            Cancel
          </Button>
          <Button disabled={!trimmed} loading={setVehicle.isPending} onClick={save}>
            Save plate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
